import { inject, Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';
import { TranslationService } from '../../shared/translation/translation.service';
import { BonusesStatuses, GroupsService } from '../../services/groups.service';
import additionalSocketsFields, { CustomNotificationChanel } from './additional-socket-fields';
import { getRandomNumber } from '../../helpers/utils';
import { CookieService, TimeService } from 'ngx-unificator/services';
import { UserService } from '../../services/user/user.service';
import { BlogService } from '../../../page/blog/blog.service';
import { BlogCategoryEnum } from '../../enums/blog-category.enum';
import { filterFirst } from 'ngx-unificator/rxjs';
import {
  CLEAR_BONUS_CODE,
  FIRST_DEP,
  INSTALL_APP_MISSION_BONUS_RECEIVED,
} from '../../services/user/data/user-group.data';
import { LanguageService } from '../../services/language/language.service';
import { UserBonusesService } from '../../services/user/user-bonuses.service';
import { MISSIONS_SLUG } from '../../services/user/data/shared-slugs';
import { OffersService } from '../../services/offers.service';
import { useStore } from 'ngx-unificator/store';
import { SpinWheelStoreEntity } from '../../store/spin-wheel.store';
import { GlobalStoreName } from '../../store';
import { BonusStage } from '../../services/user/data/user-bonuses.data';
import { WindowService } from '../../services/window.service';

export const RELOAD_BONUSES_TIME_TO_SHOW = {
  MONDAY_BONUS: {
    allowed_always: false,
    allowed_days: [1],
    title: 't.monday-bonus-title',
    description: 't.monday-bonus-description',
    prize: 't.monday-bonus-prize',
    condition: 't.monday-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/monday.png',
    bonusCode: CLEAR_BONUS_CODE,
    active: false,
  },
  FRIDAY_BONUS: {
    allowed_always: false,
    allowed_days: [5],
    title: 't.friday-bonus-title',
    description: 't.friday-bonus-description',
    prize: 't.friday-bonus-prize',
    condition: 't.friday-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/friday.png',
    bonusCode: CLEAR_BONUS_CODE,
    active: false,
  },
  TOP_SLOTS: {
    allowed_days: null,
    allowed_always: true,
    title: 't.top-slots-notify-title',
    description: 't.top-slots-notify-desc',
    prize: 't.top-slots-bonus-prize',
    condition: 't.top-slots-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/top-slots.png',
    bonusCode: 'TOP40',
    active: false,
  },
  WEEKEND_RELOAD: {
    allowed_always: false,
    allowed_days: [0, 6],
    title: 't.weekend-notify-title',
    description: 't.weekend-notify-desc',
    prize: 't.weekend-bonus-prize',
    condition: 't.weekend-bonus-condition',
    image: '/assets/img/deposit-bonus/reload-bonus/weekend.png',
    bonusCode: 'SUSHI',
    active: false,
  },
};

type Uid = {
  key: string;
  uid: number | string;
}

const NOTIFICATION_MISSION_LIST = ['phone-verification-mission', 'email-confirmation-mission', 'install-app-mission'];


@Injectable({
  providedIn: 'root',
})
export class CustomNotificationCenterService {
  private _translate = inject(TranslationService);
  private _groups = inject(GroupsService);
  private _user = inject(UserService);
  private _cookie = inject(CookieService);
  private _blog = inject(BlogService);
  private _lang = inject(LanguageService);
  private _bonuses = inject(UserBonusesService);
  private _offers = inject(OffersService);
  private _time = inject(TimeService);
  private _window = inject(WindowService);

  private _spinWheelStore = useStore<SpinWheelStoreEntity>(GlobalStoreName.SPIN_WHEEL_STORE);

  /**
   * Use for install app custom chanel
   * @private
   */
  private _installAppChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _mascotChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _vipChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _welcomeBonusesChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _reloadBonusesChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _blogChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _activeMissionChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _spinWheelChannel$: ReplaySubject<any> = new ReplaySubject<any>(1);

  private _previousBlogUid: number;
  private _previousWelcomeUid: number;
  private _previousWheelUid: number;
  private _previousMissionUid: number;
  private _installAppUid = getRandomNumber(111, 111111111111111);
  private _oldReloadUidsList: Uid[] = [];

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolvedMascothannel$: Observable<any> = this._mascotChannel$.pipe(
    distinctUntilChanged(),
    map(() => ({
      notify_channel: CustomNotificationChanel.MASCOT_NOTIFY,
      uid: getRandomNumber(111, 111111111111111),
      link: '/profile/account',
      description: 't.mascot-notify-desc',
      title: 't.mascot-notify-title',
      icon: '/assets/img/notification/mascot.png',
      templateTitle: 't.mascot-notify-btn',
      ...additionalSocketsFields[CustomNotificationChanel.MASCOT_NOTIFY],
    })),
  );

  private _resolvedVipChannel$: Observable<any> = this._vipChannel$.pipe(
    map(() => ({
      notify_channel: CustomNotificationChanel.VIP,
      uid: getRandomNumber(111, 111111111111111),
      title: 't.vip-notification-title',
      link: '/vip-program',
    })),
  );

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolvedInstallAppChannel$: Observable<any> = this._installAppChannel$.pipe(
    distinctUntilChanged(),
    map(() => ({
      notify_channel: CustomNotificationChanel.INSTALL_APP,
      uid: this._installAppUid,
      link: '/mobile-app',
      description: 't.all-games-bonuses-app',
      ...additionalSocketsFields[CustomNotificationChanel.INSTALL_APP],
    })),
  );

  public notificationSubjectList$ = [
    this._resolvedInstallAppChannel$,
    this._resolvedMascothannel$,
    this._resolvedVipChannel$,
    this._welcomeBonusesChannel$,
    this._reloadBonusesChannel$,
    this._blogChannel$,
    this._activeMissionChannel$,
    this._spinWheelChannel$,
  ];

  private _resolvedWelcomeBonusesChannel$(): Observable<any> {
    return this._bonuses.getMergedBonuses$().pipe(
      filter(() => !this._cookie.check(`welcome-bonus-${this._user.stats?.deposits_count}`)),
      map(bonuses => bonuses.find(item => item.active)),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      filter(bonus => !!bonus),
      map(bonus => {
        this._previousWelcomeUid = this._previousWelcomeUid || getRandomNumber(111, 111111111111111);

        return {
          ...bonus,
          notify_channel: CustomNotificationChanel.WELCOME_BONUSES,
          uid: this._previousWelcomeUid,
          ...additionalSocketsFields[CustomNotificationChanel.WELCOME_BONUSES],
          icon: bonus?.Image,
          title: bonus?.DescriptionModal,
          description: bonus?.Description + ' ' + bonus?.SecondDescription,
          analyticClass: 'devss-7090-welcome',
        };
      }),
      tap(bonus => this._welcomeBonusesChannel$.next(bonus)),
    );
  }

  private _resolvedReloadBonusesChannel$() {
    return of(this.getActiveReloadBonusNotification()).pipe(
      filter(() => !this._cookie.check('reload-bonus')),
      map(notifications =>
        notifications.map(n => {
          const oldUid = this._getOldUidForReloadBonuses(n?.title);
          const uid = getRandomNumber(111, 111111111111111);
          if (!oldUid) {
            this._oldReloadUidsList.push({ key: n?.title, uid });
          }

          return {
            ...n,
            notify_channel: CustomNotificationChanel.RELOAD_BONUSES,
            uid: oldUid || uid,
            ...additionalSocketsFields[CustomNotificationChanel.RELOAD_BONUSES],
            description: n.description,
            linkParams: {
              bonusCode: n?.bonusCode,
            },
          };
        }),
      ),
      tap(bonus => this._reloadBonusesChannel$.next(bonus)),
    );
  }

  private _resolveMissionWheelNotification() {
    return this._getMissions().pipe(
      filter((data) => !!data),
      switchMap((missions) => this._bonuses.freeSpinsList().pipe(map((fs) => [missions, fs]))),
      tap(([missionList, fsList]) => {
        const firstAvailableMission =
          missionList
            .filter(m =>
              NOTIFICATION_MISSION_LIST.some(pref => m.slug.startsWith(pref)) &&
              !this._groups.isExistGroup(m.FinishedGroup),
            )
            .filter(mission => (mission.FinishedGroup === INSTALL_APP_MISSION_BONUS_RECEIVED && (!this._window.isiOSDevices && !this._window.isIpadOsDevices))
              || mission.FinishedGroup !== INSTALL_APP_MISSION_BONUS_RECEIVED)
            [0];
        if (firstAvailableMission) {
          this._resolveMissionNotification(firstAvailableMission, fsList.flat());
        }
      })
    )
  }


  private _resolveMissionNotification(mission, bonusList) {
    if (!this._bonuses.isAllWelcomeBonusesActivated) {
      this._resolveActiveMissionNotification(mission);
    } else {
      const currentDay = new Date().getUTCDay();
      if (currentDay === 4 && !this._isTryTomorrowSpinWheel(bonusList)) {
        this._resolveSpinWheelNotification();
      }

      if (currentDay === 2) {
        this._resolveActiveMissionNotification(mission);
      }
    }
  }
  private _resolveActiveMissionNotification(mission: any) {
    if (this._groups.isExistGroup(FIRST_DEP)) {
      this._previousMissionUid = this._previousMissionUid || getRandomNumber(111, 111111111111111);
      this._activeMissionChannel$.next({
        notify_channel: CustomNotificationChanel.MISSIONS,
        templateTitle: 'btn.get-bonus',
        uid: this._previousMissionUid,
        description: mission.Task,
        title: mission.Title,
        icon: mission.Img,
        customClass: 'missions',
        link: mission?.BtnLink || '/missions',
      });
    }
  }

  private _resolveSpinWheelNotification() {
    this._previousWheelUid = this._previousWheelUid || getRandomNumber(111, 111111111111111);
    this._spinWheelChannel$.next({
      notify_channel: CustomNotificationChanel.SPIN_WHEEL,
      uid: this._previousWheelUid,
      description: this._spinWheelStore.storeSignal().currentPrize?.code
        ? this._translate.translate('t.spin-wheel-win-description', {
        fs: this._spinWheelStore.storeSignal().currentPrize?.fs
      }) : 't.spin-wheel-notification-description',
      title: 't.spin-wheel-notification-title',
      link: this._spinWheelStore.storeSignal().currentPrize?.code ? '/profile/deposit' : '/spin-wheel',
      ...additionalSocketsFields[CustomNotificationChanel.SPIN_WHEEL],
    })
  }

  private _blogNotification$() {
    return this._blog
      .list({
        type: BlogCategoryEnum.NOTIFICATIONS,
      })
      .pipe(
        filterFirst(data => !!data),
        map(data => data?.list?.filter(article => !this._cookie.check(article?.slug))),
        map(data =>
          data?.map(b => {
            this._previousBlogUid = this._previousBlogUid || getRandomNumber(111, 111111111111111);
            return {
              ...b,
              notify_channel: CustomNotificationChanel.BLOG,
              ...additionalSocketsFields[CustomNotificationChanel.BLOG],
              description: b.Description,
              title: b.Title,
              uid: this._previousBlogUid,
              icon: b.Image || additionalSocketsFields[b.notify_channel]?.icon,
              link: b.Link,
            };
          }),
        ),
        tap(data => this._blogChannel$.next(data)),
      );
  }

  constructor() {}

  get installAppChannel$() {
    return this._installAppChannel$;
  }
  get mascotChannel$() {
    return this._mascotChannel$;
  }

  get vipChannel$() {
    return this._vipChannel$;
  }
  get welcomeBonusesChannel$() {
    return this._welcomeBonusesChannel$;
  }
  get reloadBonusesChannel$() {
    return this._reloadBonusesChannel$;
  }

  public getActiveReloadBonusNotification(date = new Date()) {
    if (
      this._groups.isExistGroup(BonusesStatuses.ALL_WELCOME_OFFERS_USED) ||
      this._groups.isExistGroup(BonusesStatuses.ALL_SPECIAL_WELCOME_USED)
    ) {
      const day = date.getUTCDay();

      return Object.entries(RELOAD_BONUSES_TIME_TO_SHOW)
        .filter(([_, bonus]) => bonus.allowed_always || bonus.allowed_days.includes(day))
        .map(([_, { title, description, bonusCode, prize, condition, active, image }], idx) => ({
          title,
          description,
          bonusCode,
          prize,
          condition,
          active,
          image,
        }));
    } else {
      return [];
    }
  }

  public initCustomNotifications() {
    this._lang.langChange$
      .pipe(
        tap(() => {
          this._resolvedWelcomeBonusesChannel$().subscribe();
          this._resolvedReloadBonusesChannel$().subscribe();
          this._blogNotification$().subscribe();
          this._resolveMissionWheelNotification().subscribe();
        }),
      )
      .subscribe(lang => {});
  }

  private _getOldUidForReloadBonuses(key: string) {
    return this._oldReloadUidsList?.find(l => l.key === key)?.uid;
  }

  private _getMissions() {
    return this._offers.list({ category_slug: MISSIONS_SLUG }).pipe(
      filter(list => !!list),
      first(),
    );
  }

  private _isTryTomorrowSpinWheel(list) {
    return list?.some(itemToFind => {
      return itemToFind?.created_at.setHours(0, 0, 0, 0) === this._time.toLocalDate().setHours(0, 0, 0, 0) && itemToFind?.group_key?.includes('spin_wheel')
        && [BonusStage.CANCELED, BonusStage.EXPIRED, BonusStage.ACTIVATED].includes(itemToFind.stage);
    });
  }
}
